import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, User, MapPin, Scale, X, ArrowRight, UserCog, FolderOpen, Wallet,
  ChevronDown, Briefcase, Check, Plus, Bell, Radar, TrendingUp, TrendingDown,
} from "lucide-react";
import { useCompareBucket } from "../context/CompareBucketContext";
import { useOwnedGrids } from "../context/OwnedGridsContext";
import { useAuth } from "../context/AuthContext";
import {
  getCredits, listProfiles, activateProfile, getProfileSlots,
  getNotifications, markNotificationsRead,
} from "../lib/api";

// ── In-app notifications (Branch Radar monitor alerts + more) ────────────────
const NOTIF_ICON = { new_competitor: Radar, competitor_gone: Radar, demand_shift: TrendingUp };

function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  async function load() {
    try {
      const { notifications, unread } = await getNotifications();
      setItems(notifications || []);
      setUnread(unread || 0);
    } catch { /* not authed */ }
  }

  // Poll every 60s so alerts from scheduled monitors show up without a reload.
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      await markNotificationsRead();          // mark all read on open
      setUnread(0);
      setItems((xs) => xs.map((x) => ({ ...x, read: true })));
    }
  }

  function openItem(n) {
    setOpen(false);
    if (n.storeId) navigate("/branch-radar");
  }

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} title="Notifikasi"
        className="relative flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 transition-colors">
        <Bell className="w-4 h-4 text-gray-500" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-gold-500 text-white text-[9px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
          <div className="px-4 py-2.5 border-b border-gray-100">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Notifikasi</p>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-gray-400">Belum ada. Notifikasi pemantauan akan muncul di sini.</div>
            ) : items.map((n) => {
              const Icon = NOTIF_ICON[n.kind] || Bell;
              const down = n.kind === "demand_shift" && (n.meta?.delta ?? 0) < 0;
              const RealIcon = down ? TrendingDown : Icon;
              return (
                <button key={n.id} onClick={() => openItem(n)}
                  className={`w-full flex items-start gap-2.5 px-4 py-3 border-b border-gray-50 last:border-0 text-left hover:bg-gray-50 ${n.read ? "" : "bg-blue-50/40"}`}>
                  <RealIcon className={`mt-0.5 w-3.5 h-3.5 shrink-0 ${down ? "text-red-500" : "text-brand-600"}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-gray-800 leading-snug">{n.title}</p>
                    {n.body && <p className="text-[11px] text-gray-500 mt-0.5 truncate">{n.body}</p>}
                    <p className="text-[10px] text-gray-300 mt-0.5">{new Date(n.createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                  {!n.read && <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const GRID_COLORS = ["#f28b82", "#fcb97d", "#fdd663", "#81c995", "#34a853"];
const SUITABILITY  = ["Rendah", "Cukup", "Sedang", "Bagus", "Tinggi"];

function Chip({ children, blue }) {
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0 whitespace-nowrap ${
      blue ? "bg-[#e3eae8] text-[#14543e]" : "bg-gray-100 text-gray-500"
    }`}>
      {children}
    </span>
  );
}

// One profile = one business (its category is locked to it) with its own cities,
// grids, comparison bucket and store portfolio. Switching therefore swaps ALL of
// that — so we hard-reload rather than try to re-hydrate every context by hand.
function ProfileSwitcher({ profile }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [slots, setSlots] = useState(null);
  const [switching, setSwitching] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    listProfiles().then((ps) => setProfiles(ps || [])).catch(() => {});
    // Until the count is known the "new profile" button stays hidden; if it can't
    // be fetched, offer it anyway — onboarding re-checks and the server enforces.
    getProfileSlots().then(setSlots).catch(() => setSlots({ canCreate: true }));
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  async function pick(p) {
    if (p.is_active) { setOpen(false); return; }
    setSwitching(p.id);
    try {
      await activateProfile(p.id);
      window.location.reload();   // re-init every profile-scoped context cleanly
    } catch { setSwitching(null); }
  }

  const cityName = typeof profile.city === "object" ? profile.city?.nama_kabkot : profile.city;

  return (
    <div className="relative flex min-w-0 flex-1" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex min-w-0 flex-1 items-center gap-2 rounded-xl px-2 py-1.5 transition-colors hover:bg-gray-100"
        title="Ganti profil bisnis"
      >
        <MapPin className="w-3.5 h-3.5 shrink-0 text-[#14543e]" />
        {cityName && (
          <span className="hidden shrink-0 text-xs font-semibold text-[#14543e] sm:block">{cityName}</span>
        )}
        <span className="truncate text-sm font-semibold text-gray-800">
          {profile.businessName || "Tanpa nama"}
        </span>
        {profile.bizCategory && (
          <span className="hidden truncate text-xs text-gray-400 sm:block">
            {[profile.bizCategory, profile.bizSubtype].filter(Boolean).join(" – ")}
          </span>
        )}
        <ChevronDown className="w-3.5 h-3.5 shrink-0 text-gray-400" />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl">
          <div className="border-b border-gray-100 px-4 py-2.5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Profil bisnis</p>
            {slots && (
              <p className="mt-0.5 text-[11px] text-gray-400">
                {slots.used} dari {slots.max ?? "∞"} digunakan
              </p>
            )}
          </div>

          <div className="max-h-72 overflow-y-auto">
            {profiles.map((p) => (
              <button
                key={p.id}
                onClick={() => pick(p)}
                disabled={switching}
                className={`flex w-full items-start gap-2.5 border-b border-gray-50 px-4 py-2.5 text-left transition-colors last:border-0 hover:bg-gray-50 disabled:opacity-60 ${
                  p.is_active ? "bg-[#e3eae8]/50" : ""
                }`}
              >
                <Briefcase className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${p.is_active ? "text-[#14543e]" : "text-gray-300"}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-bold text-gray-800">{p.businessName || "Tanpa nama"}</p>
                  <p className="truncate text-[10px] text-gray-400">
                    {[p.bizSubtype || p.bizCategory,
                      typeof p.city === "object" ? p.city?.nama_kabkot : p.city].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
                {switching === p.id
                  ? <span className="shrink-0 text-[10px] text-gray-400">Beralih…</span>
                  : p.is_active && <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#14543e]" />}
              </button>
            ))}
          </div>

          <div className="border-t border-gray-100 p-2">
            {!slots ? null : !slots.canCreate ? (
              <p className="px-2 py-1.5 text-[10px] leading-relaxed text-gray-400">
                Batas profil tercapai. Setiap bisnis memerlukan profilnya sendiri — hubungi admin Anda untuk menaikkan batas.
              </p>
            ) : (
              <button
                onClick={() => { setOpen(false); navigate("/onboarding?new=1"); }}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-xs font-bold text-[#14543e] transition-colors hover:bg-[#e3eae8]"
              >
                <Plus className="h-3.5 w-3.5" /> Profil bisnis baru
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CompareBucket() {
  const navigate = useNavigate();
  const { bucket, remove } = useCompareBucket();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 hover:bg-gray-100/80 rounded-lg transition-colors"
        title="Keranjang perbandingan"
      >
        <Scale className="w-5 h-5 text-gray-500" />
        {bucket.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-gold-500 text-white text-[9px] font-bold flex items-center justify-center">
            {bucket.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <span className="text-xs font-bold text-gray-700">Bandingkan Grid</span>
            <span className="text-[10px] text-gray-400">{bucket.length}/6 tersimpan</span>
          </div>

          {bucket.length === 0 ? (
            <div className="px-4 py-5 text-center">
              <p className="text-xs text-gray-400">Belum ada grid tersimpan.</p>
              <p className="text-[10px] text-gray-300 mt-1">Klik grid pada peta, lalu "Tambah ke Perbandingan".</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {bucket.map((cell) => {
                const idx   = Math.min(4, Math.floor(cell.score / 20));
                const color = GRID_COLORS[idx];
                return (
                  <div key={cell.gridId} className="flex items-center gap-3 px-4 py-3">
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-gray-700 truncate">
                        {cell.kecamatanName ? `Kec. ${cell.kecamatanName}` : "Zona"}
                      </p>
                      <p className="text-xs font-mono text-gray-600 font-semibold">{cell.gridId}</p>
                    </div>
                    <span className="text-[10px] font-semibold shrink-0" style={{ color }}>
                      {SUITABILITY[idx]}
                    </span>
                    <button
                      onClick={() => remove(cell.gridId)}
                      className="text-gray-300 hover:text-red-400 transition-colors shrink-0"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {bucket.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-100">
              <button
                onClick={() => { setOpen(false); navigate("/compare"); }}
                className="w-full bg-[#14543e] hover:bg-[#104230] text-white py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-colors"
              >
                Bandingkan sekarang <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function OwnedGridsButton() {
  const navigate = useNavigate();
  const { ownedIds } = useOwnedGrids();

  return (
    <button
      onClick={() => navigate("/deep-analysis")}
      className="relative p-2 hover:bg-gray-100/80 rounded-lg transition-colors"
      title="Grid saya"
    >
      <FolderOpen className="w-5 h-5 text-gray-500" />
      {ownedIds.length > 0 && (
        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-gold-500 text-white text-[9px] font-bold flex items-center justify-center">
          {ownedIds.length}
        </span>
      )}
    </button>
  );
}

function UserMenu() {
  const navigate = useNavigate();
  const { logout: authLogout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  async function logout() {
    await authLogout();   // revokes refresh token server-side + clears caches
    navigate("/auth");
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-9 h-9 rounded-full bg-[#14543e] flex items-center justify-center hover:bg-[#104230] transition-colors"
        title="Akun"
      >
        <User className="w-5 h-5 text-white" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden">
          <button
            onClick={() => { setOpen(false); navigate("/profile"); }}
            className="w-full flex items-center gap-3 px-4 py-3.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <UserCog className="w-4 h-4 text-gray-400 shrink-0" />
            Kelola Profil
          </button>
          <div className="h-px bg-gray-100 mx-3" />
          <button
            onClick={() => { setOpen(false); navigate("/account"); }}
            className="w-full flex items-center gap-3 px-4 py-3.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Wallet className="w-4 h-4 text-gray-400 shrink-0" />
            Kredit &amp; Tagihan
          </button>
          <div className="h-px bg-gray-100 mx-3" />
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-3.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            Keluar
          </button>
        </div>
      )}
    </div>
  );
}

function BalanceChip() {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [bump, setBump] = useState(false);

  useEffect(() => {
    let prev = null;
    const load = () => getCredits().then((c) => {
      setBalance(() => {
        if (prev != null && c.balance !== prev) { setBump(true); setTimeout(() => setBump(false), 600); }
        prev = c.balance;
        return c.balance;
      });
    }).catch(() => {});
    load();
    // Refresh instantly after any purchase, and when the tab regains focus.
    const onChange = () => load();
    window.addEventListener("landhep:balance", onChange);
    window.addEventListener("focus", onChange);
    return () => {
      window.removeEventListener("landhep:balance", onChange);
      window.removeEventListener("focus", onChange);
    };
  }, []);

  if (balance == null) return null;
  return (
    <button
      onClick={() => navigate("/account")}
      title="Kredit & Tagihan"
      className={`hidden sm:flex items-center gap-1.5 px-3 h-9 rounded-full border transition-all duration-300 ${
        bump ? "bg-brand-100 border-brand-300 scale-105" : "bg-teal-50 hover:bg-brand-100 border-brand-100"
      }`}
    >
      <Wallet className="w-3.5 h-3.5 text-brand-600" />
      <span className="text-xs font-bold text-brand-700 tabular-nums">Rp {Number(balance).toLocaleString("id-ID")}</span>
    </button>
  );
}

function Navbar() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    try {
      const p = JSON.parse(localStorage.getItem("landhep_profile") || "{}");
      if (p?.businessName || p?.bizCategory) setProfile(p);
    } catch {}
  }, []);

  return (
    <nav className="absolute top-2.5 left-2.5 right-2.5 z-50 h-16 flex items-center gap-4 pl-0 pr-6 bg-white/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-100/50">
      <button
        type="button"
        onClick={() => navigate("/analysis")}
        /* The one solid-brand element in the bar: flush to its left edge and full
           height, with only the left corners rounded so it reads as part of the bar
           rather than a chip sitting on it. The rest of the bar stays light so the map
           keeps showing through. Rounding is on the button, not overflow-hidden on the
           nav — that would clip the dropdowns. The -1px insets pull it over the
           bar's own border, which otherwise shows as a pale line across the green. */
        className="-my-px -ml-px self-stretch shrink-0 w-[148px] flex items-center justify-center rounded-l-2xl bg-brand-700 gap-2 text-base font-semibold tracking-tight text-white transition-colors hover:bg-brand-800"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white">
          <img src="/landhep-icon.png" alt="" className="h-5 w-5" />
        </span>
        Landhep
      </button>

      {profile && (
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <ProfileSwitcher profile={profile} />

          <div className="flex items-center gap-1.5 shrink-0 hidden md:flex">
            {profile.businessFormat && <Chip>{profile.businessFormat}</Chip>}
            {profile.priceRange    && <Chip blue>{profile.priceRange}</Chip>}
            {profile.incomeLevel   && <Chip blue>Pendapatan {profile.incomeLevel}</Chip>}
            {profile.gridResolution && <Chip>{profile.gridResolution === "100m" ? "Grid 100 m" : "Grid 1 km"}</Chip>}
            {profile.priorities?.slice(0, 2).map((p) => (
              <Chip key={p}>{p}</Chip>
            ))}
            {profile.priorities?.length > 2 && (
              <span className="text-[10px] text-gray-400">+{profile.priorities.length - 2}</span>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 ml-auto">
        <BalanceChip />
        <NotificationBell />
        <CompareBucket />
        <OwnedGridsButton />
        <UserMenu />
      </div>
    </nav>
  );
}

export default Navbar;
